import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { useSave } from "@ncp/ui";
import type { FormBindings, FormRule, FormSchema, UiSchema } from "@ncp/forms";
import type { ChangeSet, JsonPatchOp } from "../releases/api";

export type DefinitionStatus = "Draft" | "InReview" | "Published" | "Retired";
export type FormPurpose = "Intake" | "Rfi" | "Objection" | "Internal";

/** One catalogue row (per key): published + draft versions folded together. */
export interface FormSummary {
  key: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  tags: string[];
  publishedVersion?: number | null;
  publishedId?: string | null;
  publishedAt?: string | null;
  draftVersion?: number | null;
  draftId?: string | null;
  draftStatus?: DefinitionStatus | null;
  latestVersion: number;
  versionCount: number;
  updatedAt: string;
  updatedByName?: string | null;
  sourcePackageKey?: string | null;
  purpose: FormPurpose;
  fieldCount: number;
  serviceKeys: string[];
}

export interface FormDefinition {
  id: string;
  key: string;
  version: number;
  status: DefinitionStatus;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  tags: string[];
  schema: FormSchema;
  uiSchema: UiSchema;
  bindings: FormBindings | null;
  rules: FormRule[] | null;
  purpose: FormPurpose;
  serviceKeys: string[];
  sampleData: Record<string, unknown> | null;
  fieldCount: number;
  changeNote?: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string | null;
  publishedByName?: string | null;
  publishedByChangeSetId?: string | null;
  sourcePackageKey?: string | null;
}

export interface DefinitionVersion {
  id: string;
  version: number;
  status: DefinitionStatus;
  changeNote?: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string | null;
  publishedByName?: string | null;
  publishedByChangeSetId?: string | null;
}

export interface DefinitionDiff {
  key: string;
  fromVersion: number;
  toVersion: number;
  beforeJson?: string | null;
  afterJson: string;
  patchJson: string;
  summary: string[];
}

export interface FormLint { errors: string[]; warnings: string[]; fieldCount: number }
export interface ServerIssue { path: string; message: string; messageAr?: string | null; severity: "error" | "warning" | string; keyword?: string | null; ruleKey?: string | null }
export interface ServerValidation { valid: boolean; issues: ServerIssue[] }

/** The editable content of a form version – what the builder holds in memory and sends as the payload body. */
export interface FormBody {
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  tags: string[];
  purpose: FormPurpose;
  serviceKeys: string[];
  schema: FormSchema;
  uiSchema: UiSchema;
  rules: FormRule[];
  bindings: FormBindings;
  sampleData: Record<string, unknown> | null;
}

export function bodyOf(d: FormDefinition): FormBody {
  return {
    name: d.name,
    nameAr: d.nameAr ?? null,
    description: d.description ?? null,
    descriptionAr: d.descriptionAr ?? null,
    tags: d.tags ?? [],
    purpose: d.purpose,
    serviceKeys: d.serviceKeys ?? [],
    schema: d.schema,
    uiSchema: d.uiSchema,
    rules: d.rules ?? [],
    bindings: d.bindings ?? {},
    sampleData: d.sampleData ?? null,
  };
}

const KEY = ["platform", "forms"] as const;

export function useForms(params: { q?: string; tag?: string; status?: string } = {}) {
  const api = useApi();
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.tag) qs.set("tag", params.tag);
  if (params.status) qs.set("status", params.status);
  const suffix = qs.toString() ? `?${qs}` : "";
  return useQuery({ queryKey: [...KEY, "list", params], queryFn: () => api.get<FormSummary[]>(`/api/platform/forms${suffix}`), staleTime: 10_000 });
}

export function useFormDefinition(key: string | undefined, version?: number) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEY, "one", key, version ?? "latest"],
    queryFn: () => api.get<FormDefinition>(`/api/platform/forms/${encodeURIComponent(key!)}${version ? `?version=${version}` : ""}`),
    enabled: !!key,
    staleTime: 5_000,
  });
}

export function useFormVersions(key: string | undefined) {
  const api = useApi();
  return useQuery({ queryKey: [...KEY, "versions", key], queryFn: () => api.get<DefinitionVersion[]>(`/api/platform/forms/${encodeURIComponent(key!)}/versions`), enabled: !!key, staleTime: 5_000 });
}

export function useFormDiff(key: string | undefined, from: number | undefined, to: number | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEY, "diff", key, from, to],
    queryFn: () => api.get<DefinitionDiff>(`/api/platform/forms/${encodeURIComponent(key!)}/diff?from=${from}&to=${to}`),
    enabled: !!key && from !== undefined && to !== undefined && from !== to,
    staleTime: 60_000,
  });
}

export function useFormUsedBy(key: string | undefined) {
  const api = useApi();
  return useQuery({ queryKey: [...KEY, "usedBy", key], queryFn: () => api.get<Array<{ key: string; name: string; nameAr?: string | null; status: string; version: number }>>(`/api/platform/forms/${encodeURIComponent(key!)}/used-by`), enabled: !!key, staleTime: 30_000 });
}

export function useFormActions() {
  const api = useApi();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: [...KEY] });
    void qc.invalidateQueries({ queryKey: ["platform", "definitions", "forms"] });
    void qc.invalidateQueries({ queryKey: ["platform", "change-sets"] });
  };
  return {
    create: useSave({ mutationFn: (input: { key: string; body: FormBody; changeNote?: string }) => api.post<FormDefinition>("/api/platform/forms", input), onSuccess: invalidate }),
    update: useSave({ mutationFn: (input: { id: string; body: FormBody; changeNote?: string }) => api.put<FormDefinition>(`/api/platform/forms/${input.id}`, { body: input.body, changeNote: input.changeNote }), onSuccess: invalidate }),
    newVersion: useSave({ mutationFn: (input: { key: string; changeNote?: string }) => api.post<FormDefinition>(`/api/platform/forms/${encodeURIComponent(input.key)}/versions`, { changeNote: input.changeNote }), onSuccess: invalidate }),
    discard: useSave({ mutationFn: (id: string) => api.delete<void>(`/api/platform/forms/${id}`), onSuccess: invalidate }),
    stage: useSave({ mutationFn: (input: { id: string; titleHint?: string }) => api.post<ChangeSet>(`/api/platform/forms/${input.id}/stage`, { titleHint: input.titleHint }), onSuccess: invalidate }),
    withdraw: useSave({ mutationFn: (id: string) => api.post<FormDefinition>(`/api/platform/forms/${id}/withdraw`), onSuccess: invalidate }),
    // Checks that store nothing: a failure is not "Not saved".
    lint: useSave({ mutationFn: (input: { schema: FormSchema; uiSchema: UiSchema; rules: FormRule[] }) => api.post<FormLint>("/api/platform/forms/lint", input), failed: t("admin:common.error") }),
    validate: useSave({ mutationFn: (input: { id: string; data: unknown; locale: string }) => api.post<ServerValidation>(`/api/platform/forms/${input.id}/validate`, { data: input.data, locale: input.locale }), failed: t("admin:common.error") }),
  };
}

export type { JsonPatchOp };
